/**
 * User Display Name Utilities
 *
 * Centralized logic for formatting user display names consistently across the application.
 */

import type { UserProfile, User } from '../../types';

/**
 * Minimal user data required for display purposes.
 * Used when we only have partial profile data (e.g., during authentication flows).
 * Matches the UserProfileInfo schema from the backend API.
 */
export interface UserDisplayData {
  email: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface UserDisplayOptions {
  /**
   * Use only the first name for a more casual feel (e.g., "Welcome, William")
   * Only applies when firstName is available
   * @default false
   */
  firstNameOnly?: boolean;

  /**
   * Fallback string to return if no user data is available
   * @default 'User'
   */
  fallback?: string;
}

/**
 * Get the user's display name with fallback priority:
 * 1. displayName (if explicitly set)
 * 2. firstName + lastName (constructed)
 * 3. firstName only (if lastName is missing)
 * 4. email (last resort if names are missing)
 * 5. fallback string (if user is null/undefined)
 *
 * @param user - User or UserProfile object (can be null/undefined)
 * @param options - Display options for customizing output
 * @returns Formatted display name
 *
 * @example
 * // Standard display
 * getUserDisplayName(user) // "John Smith" or "john@example.com"
 *
 * @example
 * // Casual greeting with first name only
 * getUserDisplayName(user, { firstNameOnly: true }) // "John"
 *
 * @example
 * // Custom fallback
 * getUserDisplayName(null, { fallback: 'Guest' }) // "Guest"
 */
export function getUserDisplayName(
  user: UserProfile | User | UserDisplayData | null | undefined,
  options: UserDisplayOptions = {},
): string {
  const { firstNameOnly = false, fallback = 'User' } = options;

  if (!user) return fallback;

  // For UserProfile with explicit displayName
  if ('displayName' in user && user.displayName) {
    // If firstNameOnly is requested and we have firstName, prefer that over displayName
    if (firstNameOnly && 'firstName' in user && user.firstName) {
      return user.firstName;
    }
    return user.displayName;
  }

  // Build from first + last name (if available in UserProfile)
  if ('firstName' in user || 'lastName' in user) {
    const firstName = 'firstName' in user ? user.firstName : null;
    const lastName = 'lastName' in user ? user.lastName : null;

    // First name only mode
    if (firstNameOnly && firstName) {
      return firstName;
    }

    // Full name mode
    if (firstName || lastName) {
      return `${firstName || ''} ${lastName || ''}`.trim();
    }
  }

  // Last resort: email
  return user.email;
}

/**
 * Get the initial for the user avatar based on their display name.
 * Returns the first character of the display name in uppercase.
 *
 * @param user - User or UserProfile object (can be null/undefined)
 * @param fallback - Fallback character if no user data
 * @returns Single uppercase character for avatar
 *
 * @example
 * getUserInitial(user) // "J" (from "John Smith")
 * getUserInitial(null) // "U"
 */
export function getUserInitial(
  user: UserProfile | User | UserDisplayData | null | undefined,
  fallback = 'U',
): string {
  if (!user) return fallback;
  const displayName = getUserDisplayName(user);
  return displayName.charAt(0).toUpperCase();
}

/**
 * Get a casual greeting with the user's first name.
 * Convenience wrapper around getUserDisplayName with firstNameOnly option.
 *
 * @param user - User or UserProfile object (can be null/undefined)
 * @param prefix - Optional greeting prefix (e.g., "Welcome", "Hello")
 * @returns Formatted greeting string
 *
 * @example
 * getUserGreeting(user) // "William"
 * getUserGreeting(user, "Welcome") // "Welcome, William"
 * getUserGreeting(user, "Hello") // "Hello, William"
 */
export function getUserGreeting(
  user: UserProfile | User | UserDisplayData | null | undefined,
  prefix?: string,
): string {
  const name = getUserDisplayName(user, { firstNameOnly: true });

  if (prefix) {
    return `${prefix}, ${name}`;
  }

  return name;
}
